import {
  loginIcon,
  ExcalLogo,
  eyeIcon,
} from "@excalidraw/excalidraw/components/icons";
import { MainMenu } from "@excalidraw/excalidraw/index";
import React from "react";

import { isDevEnv } from "@excalidraw/common";

import type { Theme } from "@excalidraw/element/types";

import { LanguageList } from "../app-language/LanguageList";
import { isExcalidrawPlusSignedUser } from "../app_constants";
import { useAtomValue, useSetAtom } from "../app-jotai";
import {
  authDialogAtom,
  currentUserAtom,
  dashboardDialogAtom,
  isWorkspaceEnabled,
  workspaceApi,
} from "../data/workspace";

import { saveDebugState } from "./DebugCanvas";

export const AppMainMenu: React.FC<{
  onCollabDialogOpen: () => any;
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  theme: Theme | "system";
  refresh: () => void;
}> = React.memo((props) => {
  const workspaceEnabled = isWorkspaceEnabled();
  const currentUser = useAtomValue(currentUserAtom);
  const setCurrentUser = useSetAtom(currentUserAtom);
  const openAuthDialog = useSetAtom(authDialogAtom);
  const openDashboard = useSetAtom(dashboardDialogAtom);

  return (
    <MainMenu>
      <MainMenu.DefaultItems.LoadScene />
      <MainMenu.DefaultItems.SaveToActiveFile />
      <MainMenu.DefaultItems.Export />
      <MainMenu.DefaultItems.SaveAsImage />
      {props.isCollabEnabled && (
        <MainMenu.DefaultItems.LiveCollaborationTrigger
          isCollaborating={props.isCollaborating}
          onSelect={() => props.onCollabDialogOpen()}
        />
      )}
      <MainMenu.DefaultItems.CommandPalette className="highlighted" />
      <MainMenu.DefaultItems.SearchMenu />
      <MainMenu.DefaultItems.Help />
      <MainMenu.DefaultItems.ClearCanvas />
      <MainMenu.Separator />
      {workspaceEnabled ? (
        <>
          {currentUser ? (
            <>
              <MainMenu.Item
                icon={ExcalLogo}
                onSelect={() => openDashboard(true)}
                className="highlighted"
              >
                My scenes
              </MainMenu.Item>
              <MainMenu.DefaultItems.Socials />
              <MainMenu.Item
                icon={loginIcon}
                onSelect={async () => {
                  await workspaceApi.logout();
                  setCurrentUser(null);
                }}
              >
                Sign out ({currentUser.displayName})
              </MainMenu.Item>
            </>
          ) : (
            <>
              <MainMenu.DefaultItems.Socials />
              <MainMenu.Item
                icon={loginIcon}
                onSelect={() => openAuthDialog(true)}
                className="highlighted"
              >
                Sign in
              </MainMenu.Item>
            </>
          )}
        </>
      ) : (
        <>
          <MainMenu.ItemLink
            icon={ExcalLogo}
            href={`${
              import.meta.env.VITE_APP_PLUS_LP
            }/plus?utm_source=excalidraw&utm_medium=app&utm_content=hamburger`}
            className=""
          >
            Excalidraw+
          </MainMenu.ItemLink>
          <MainMenu.DefaultItems.Socials />
          <MainMenu.ItemLink
            icon={loginIcon}
            href={`${import.meta.env.VITE_APP_PLUS_APP}${
              isExcalidrawPlusSignedUser ? "" : "/sign-up"
            }?utm_source=signin&utm_medium=app&utm_content=hamburger`}
            className="highlighted"
          >
            {isExcalidrawPlusSignedUser ? "Sign in" : "Sign up"}
          </MainMenu.ItemLink>
        </>
      )}
      {isDevEnv() && (
        <MainMenu.Item
          icon={eyeIcon}
          onSelect={() => {
            if (window.visualDebug) {
              delete window.visualDebug;
              saveDebugState({ enabled: false });
            } else {
              window.visualDebug = { data: [] };
              saveDebugState({ enabled: true });
            }
            props?.refresh();
          }}
        >
          Visual Debug
        </MainMenu.Item>
      )}
      <MainMenu.Separator />
      <MainMenu.DefaultItems.Preferences />
      <MainMenu.DefaultItems.ToggleTheme allowSystemTheme theme={props.theme} />
      <MainMenu.ItemCustom>
        <LanguageList style={{ width: "100%" }} />
      </MainMenu.ItemCustom>
      <MainMenu.DefaultItems.ChangeCanvasBackground />
    </MainMenu>
  );
});
